import { useState } from "react";
import { defineMessages, FormattedMessage, useIntl } from "react-intl";

import { useActiveOrganization } from "common/account/active_organization";
import { useQuery, useMutation } from "util/graphql";
import type { TimeSlotType as TimeSlotInput } from "graphql_globals";
import { pushNotification } from "common/core/notification_center/actions";
import { NOTIFICATION_TYPES } from "constants/notifications";
import LoadingIndicator from "common/core/loading_indicator";
import { MutationErrorModal } from "common/settingsv2/modals/mutation_error_modal";
import { SettingsTitle } from "common/settingsv2/common";
import { isOrgAdmin } from "util/organization";

import OrganizationScheduleQuery from "./schedule_query.graphql";
import UpdateOrganizationScheduleMutation from "./update_organization_schedule_mutation.graphql";
import InternalOrganizationScheduleQuery, {
  type InternalOrganizationSchedule,
  type InternalOrganizationScheduleVariables,
} from "./internal_schedule_query.graphql";
import CreateOrganizationScheduleMutation from "./create_organization_schedule_mutation.graphql";
import HoursForm, { type FormValues } from "./hours_form";

const MESSAGES = defineMessages({
  notaryHoursSuccess: {
    id: "cb020c11-d585-4882-88d7-60aff1094aec",
    defaultMessage: "Notary hours successfully updated",
  },
});

export function NotaryHoursSetting() {
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<"error" | "success" | null>(null);
  const [activeOrganizationId] = useActiveOrganization();

  const intl = useIntl();

  const { data, loading } = useQuery(OrganizationScheduleQuery, {
    variables: { organizationId: activeOrganizationId! },
  });

  const updateScheduleMutation = useMutation(UpdateOrganizationScheduleMutation, {
    update(cacheProxy, { data }) {
      const result = cacheProxy.readQuery<
        InternalOrganizationSchedule,
        InternalOrganizationScheduleVariables
      >({
        query: InternalOrganizationScheduleQuery,
        variables: { organizationId: activeOrganizationId! },
      })!;
      const { organization } = result;
      if (organization?.__typename !== "Organization") {
        throw new Error(`Expected Organization, got ${organization?.__typename}`);
      }

      cacheProxy.writeQuery({
        query: InternalOrganizationScheduleQuery,
        variables: {
          organizationId: activeOrganizationId,
        },
        data: {
          ...result,
          organization: {
            ...organization,
            schedule: {
              ...organization.schedule,
              ...data!.updateOrganizationSchedule?.organizationSchedule,
            },
          },
        },
      });
    },
  });

  const createScheduleMutation = useMutation(CreateOrganizationScheduleMutation, {
    update(cacheProxy, { data }) {
      const result = cacheProxy.readQuery<
        InternalOrganizationSchedule,
        InternalOrganizationScheduleVariables
      >({
        query: InternalOrganizationScheduleQuery,
        variables: { organizationId: activeOrganizationId! },
      })!;
      const { organization } = result;
      if (organization?.__typename !== "Organization") {
        throw new Error(`Expected Organization, got ${organization?.__typename}`);
      }

      cacheProxy.writeQuery({
        query: InternalOrganizationScheduleQuery,
        variables: {
          organizationId: activeOrganizationId,
        },
        data: {
          ...result,
          organization: {
            ...organization,
            schedule: {
              ...organization.schedule,
              ...data!.createOrganizationSchedule?.organizationSchedule,
            },
          },
        },
      });
    },
  });

  if (loading) {
    return <LoadingIndicator />;
  }

  const organization = data?.organization;
  if (organization?.__typename !== "Organization") {
    throw new Error(`Expected Organization, got ${organization?.__typename}`);
  }
  const saveMutation = organization.schedule ? updateScheduleMutation : createScheduleMutation;

  if (!organization.notarySchedulingEnabled) {
    return null;
  }

  const userRole = data?.viewer.user?.organizationMembership?.role;
  const userCanUpdate = userRole && isOrgAdmin(userRole);

  function saveSchedule(formValues: FormValues) {
    setSubmitting(true);
    const timeSlots = Object.entries(formValues).reduce((acc, [key, value]) => {
      if (key.includes("day") && value) {
        acc.push({
          day: key,
          startTime: Number(formValues.startTime),
          endTime: Number(formValues.endTime),
        });
      }
      return acc;
    }, [] as TimeSlotInput[]);
    return saveMutation({
      variables: {
        input: {
          organizationId: activeOrganizationId!,
          timezone: formValues.timezone,
          timeSlots,
        },
      },
    })
      .then(() => {
        setStatus("success");
        pushNotification({
          type: NOTIFICATION_TYPES.DEFAULT,
          message: intl.formatMessage(MESSAGES.notaryHoursSuccess),
        });
      })
      .catch(() => setStatus("error"))
      .finally(() => setSubmitting(false));
  }

  return (
    <>
      <SettingsTitle>
        <FormattedMessage id="50548780-b16b-45a9-b97e-480e53331dc2" defaultMessage="Notary Hours" />
      </SettingsTitle>

      <HoursForm
        organization={organization}
        onSubmit={saveSchedule}
        submitting={submitting}
        userCanUpdate={Boolean(userCanUpdate)}
      />
      {status === "error" && <MutationErrorModal onClick={() => setStatus(null)} />}
    </>
  );
}
